import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BankTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    transaction_date: '',
    transaction_type: 'credit',
    amount: 0,
    reference_number: '',
    description: '',
    customer_id: '',
    invoice_id: ''
  });

  useEffect(() => {
    fetchTransactions();
    fetchCustomers();
    fetchInvoices();
  }, []);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bank-transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...formData,
        customer_id: formData.customer_id || null,
        invoice_id: formData.invoice_id || null
      };
      await axios.post(`${API}/bank-transactions`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction recorded successfully');
      setOpen(false);
      resetForm();
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to record transaction');
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_date: '',
      transaction_type: 'credit',
      amount: 0,
      reference_number: '',
      description: '',
      customer_id: '',
      invoice_id: ''
    });
  };

  const getCustomerName = (customerId) => {
    if (!customerId) return '-';
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const getInvoiceNumber = (invoiceId) => {
    if (!invoiceId) return '-';
    const invoice = invoices.find(i => i.id === invoiceId);
    return invoice ? invoice.invoice_number : 'Unknown';
  };

  const calculateBalance = () => {
    return transactions.reduce((acc, txn) => {
      return txn.transaction_type === 'credit' ? acc + txn.amount : acc - txn.amount;
    }, 0);
  };

  return (
    <div data-testid="bank-transactions-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Bank Transactions</h1>
          <p className="text-slate-600 mt-1">Manually record bank transactions</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-transaction-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Add Transaction
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="transaction-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record Bank Transaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="transaction_date">Transaction Date *</Label>
                <Input
                  id="transaction_date"
                  data-testid="transaction-date-input"
                  type="date"
                  value={formData.transaction_date}
                  onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="transaction_type">Transaction Type *</Label>
                <Select value={formData.transaction_type} onValueChange={(value) => setFormData({ ...formData, transaction_type: value })}>
                  <SelectTrigger data-testid="transaction-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Credit (Money In)</SelectItem>
                    <SelectItem value="debit">Debit (Money Out)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  data-testid="amount-input"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                  required
                  step="0.01"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reference_number">Reference Number *</Label>
                <Input
                  id="reference_number"
                  data-testid="reference-number-input"
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer">Customer (Optional)</Label>
                <Select value={formData.customer_id} onValueChange={(value) => setFormData({ ...formData, customer_id: value })}>
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="invoice">Invoice (Optional)</Label>
                <Select value={formData.invoice_id} onValueChange={(value) => setFormData({ ...formData, invoice_id: value })}>
                  <SelectTrigger data-testid="invoice-select">
                    <SelectValue placeholder="Select invoice" />
                  </SelectTrigger>
                  <SelectContent>
                    {invoices.map((invoice) => (
                      <SelectItem key={invoice.id} value={invoice.id}>
                        {invoice.invoice_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-transaction-button" type="submit" className="w-full">
                Record Transaction
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-slate-900">Current Balance</span>
          <span className="text-2xl font-bold text-blue-600" data-testid="current-balance">
            Tsh {calculateBalance().toFixed(2)}
          </span>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500">
                  No transactions found. Add your first transaction.
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((txn) => (
                <TableRow key={txn.id} data-testid={`transaction-row-${txn.id}`}>
                  <TableCell>{new Date(txn.transaction_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Badge className={txn.transaction_type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} variant="outline">
                      {txn.transaction_type}
                    </Badge>
                  </TableCell>
                  <TableCell>{txn.reference_number}</TableCell>
                  <TableCell>{txn.description}</TableCell>
                  <TableCell>{getCustomerName(txn.customer_id)}</TableCell>
                  <TableCell>{getInvoiceNumber(txn.invoice_id)}</TableCell>
                  <TableCell className={txn.transaction_type === 'credit' ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                    {txn.transaction_type === 'credit' ? '+' : '-'}Tsh {txn.amount.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
